import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import DlsLink from "@galaxy-io/dls/links/Link";
import Text, { TextSize, TextWeight } from "@galaxy-io/dls/text/Text";
import { pageNumber } from "@gtm/contracts";
import { companySortKeys } from "@gtm/contracts/core/sorting";
import { XIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useUrlState } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FeedViewport, SearchForm } from "@/components/workspace/people-styles";
import { RecordTable } from "@/components/workspace/record-table";
import { Toolbar } from "@/components/workspace/toolbar";
import { useTableSort } from "@/components/workspace/use-table-sort";
import { DirectoryFilterBuilder } from "@/features/people/directory-filter-builder";
import { apiClient, unwrap } from "@/lib/api-client";
import { COMPANY_FILTER_KEYS } from "@/lib/filters";
import { date, safeUrl } from "@/lib/utils";
import { CompanyLink } from "./company-link";

export function CompanyDirectory() {
  const sort = useTableSort(companySortKeys);
  const { params, update } = useUrlState();
  const filter = params.get("filter") || undefined;
  const tags = params.get("tags") || "";
  const domain = params.get("domain") || "";
  const assignee = params.get("assignee") || "";
  const q = params.get("q") || "",
    page = pageNumber(params.get("page") || undefined);
  const result = useQuery({
    queryKey: ["companies", filter, q, domain, tags, assignee, page, sort.query],
    queryFn: ({ signal }) =>
      unwrap(
        apiClient().GET("/companies", {
          params: {
            query: { filter, q, domain, tags, assignee, page: String(page), ...sort.query },
          },
          signal,
        }),
      ),
  });
  const filters = { filter, q, domain, tags, assignee };
  function apply(values: Record<string, string>) {
    update({
      ...Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value || null])),
      page: null,
      companyId: null,
      personId: null,
    });
  }
  return (
    <>
      <Toolbar>
        <SearchForm
          onSubmit={(event) => {
            event.preventDefault();
            apply({ q: String(new FormData(event.currentTarget).get("q") || "") });
          }}
        >
          <Input
            key={q}
            name="q"
            defaultValue={q}
            aria-label="Search companies"
            placeholder="Search companies, domains, tags…"
            type="search"
            size={InputSize.SMALL}
            label="Search companies"
            hideLabel
          />
        </SearchForm>
        <DirectoryFilterBuilder kind="company" query={filters} onApply={apply} />
        {COMPANY_FILTER_KEYS.some((key) => filters[key]) && (
          <Button
            label="Clear"
            ariaLabel="Clear company filters"
            icon={XIcon}
            variant={ButtonVariant.TERTIARY}
            onClick={() => apply(Object.fromEntries(COMPANY_FILTER_KEYS.map((key) => [key, ""])))}
            size={ButtonSize.SMALL}
            tooltip="Clear company filters"
          />
        )}
      </Toolbar>
      <FeedViewport>
        <RecordTable
          sort={sort}
          label="Companies"
          auditKind="company"
          rowKeys={(result.data?.items || []).map((c) => c.id)}
          isLoading={result.isPending || result.isFetching}
          error={result.error || undefined}
          onRetry={() => void result.refetch()}
          onRowClick={(id) =>
            update({
              companyId: id,
              companyTab: null,
              companyPage: null,
              personId: null,
              personTab: null,
              activityId: null,
              runId: null,
            })
          }
          paging={{
            page,
            total: result.data?.total,
            hasPrevious: page > 1,
            hasNext: page * 40 < (result.data?.total || 0),
            busy: result.isFetching || !!result.error,
            onPrevious: () => update({ page: String(page - 1) }),
            onNext: () => update({ page: String(page + 1) }),
          }}
          columns={[
            { key: "name", label: "Company", width: "auto" },
            { key: "domain", label: "Domain", width: 240 },
            { key: "tags", label: "Tags", width: 220 },
            { key: "people", label: "People", width: 120, align: "right" as const },
            { key: "created", label: "Discovered", width: 220, align: "right" as const },
          ]}
          rows={(result.data?.items || []).map((company) => ({
            name: (
              <CompanyLink id={company.id}>
                <Text size={TextSize.BODY_MD} weight={TextWeight.MEDIUM} lineClamp={1}>
                  {company.name}
                </Text>
              </CompanyLink>
            ),
            domain: safeUrl(company.url || company.domain || "") ? (
              <DlsLink href={safeUrl(company.url || company.domain || "")!} isExternal>
                {company.domain || company.url}
              </DlsLink>
            ) : (
              company.domain
            ),
            tags: company.tags.map((tag) => tag.name).join(", ") || "—",
            people: company.peopleCount,
            created: date(company.createdAt, true),
          }))}
        />
      </FeedViewport>
    </>
  );
}
